import { Challenger, Score, TeamReference } from "./pdfData";
import {
  CurrentLineups,
  Player,
  Sanction,
  Substitution,
  TeamSides,
  TeamSquads,
} from "./gameGeneral";
import { InitializeMatchPayload } from "./socketIo/initializeMatchPayload";
import {
  GenericUpdateAction,
  UpdateActionType,
  UpdateActionWithState,
} from "./socketIo/updateAction";
import { UpdateActionWithStateDict } from "./socketIo/socketIo";
import { DeepReadonly } from "@/models/utils";

export type GameState = {
  actionHistory: UpdateActionWithStateDict;
  arbitration: InitializeMatchPayload["arbitration"];
  comments: string;
  currentLineups: CurrentLineups;
  currentScore: Score;
  currentSet: SetStat;
  currentSetScore: Score;
  expectedNextSetStartTime?: number;
  gameProgress: GameProgress;
  gender: InitializeMatchPayload["matchSeries"]["gender"];
  initialServingTeam: TeamReference;
  isChallengeOngoing: boolean;
  isChecksumValid: boolean;
  isSetPauseOngoing: boolean;
  isSetterPlayerKnown: boolean;
  lastAction: UpdateActionWithState;
  lastVisibleAction: UpdateActionWithState;
  lastChallenge?: LastChallenge;
  lastTimeout?: LastTimeout;
  liberoExchangePlayer: { [key in TeamReference]?: Player };
  location: InitializeMatchPayload["location"];
  matchConfiguration: InitializeMatchPayload["matchConfiguration"];
  matchNumber: InitializeMatchPayload["matchNumber"];
  numberOfSpectators: number;
  playedSets: SetStats;
  samsId: string;
  samsScoreVersion: string;
  sanctions: Sanctions;
  scoreConfiguration: InitializeMatchPayload["scoreConfiguration"];
  scoreHistory: ScoreHistory;
  scoresheetPdfUrl: string;
  serving: TeamReference;
  startTime: number;
  substitutions: Substitutions;
  timeFirstScoreInMatch: number;
  teamConfirmations: TeamConfirmations;
  teamSides: TeamSides;
  teamSquads: TeamSquads;
  teams: Teams;
  unknownUpdateActionTypes: Map<UpdateActionType, object[]>;
};

export enum GameProgress {
  CREATED,
  INITIALIZED,
  ARBITRATION_CONFIRMED,
  STARTING_SIX_LOCKED,
  GAME_STARTED,
  GAME_FINISHED,
  GAME_FINALIZED,
  FINALIZATION_FAILED,
  FINALIZATION_SENT,
}

export type Teams = Pick<InitializeMatchPayload, TeamReference>;

export type SetStats = {
  [key in number]: SetStat;
};

export type SetStat = {
  setNumber: number;
  result: Score;
  timeoutsUsed: Score;
  timeoutsRemaining: Score;
  substitutionsUsed: Score;
  substitutionsRemaining: Score;
  challengesUsed: Score;
  challengesRemaining: Score;
  startTime?: number;
  endTime?: number;
  exceptionalSubstitutionsUsed: Score;
};

export type ScoreHistory = {
  [key in number]: SetScoreHistory;
};

type SetScoreHistory = [number, number, number][];

export type ScoreHistoryEntry = {
  setNumber: number;
  score: Score;
};

export type TeamConfirmations = {
  [key in Challenger]: Confirmation;
};

export enum Confirmation {
  NONE,
  LOADED,
  SQUAD,
  SQUAD_RELOADED,
  GAME,
}

export type UpdateGameStateFunction<T extends UpdateActionType> = (
  action: GenericUpdateAction<T>,
  state: DeepReadonly<GameState>,
) => DeepReadonly<Partial<GameState>>;

export type LastTimeout = {
  teamCode: TeamReference;
  timeoutEndTimestamp: number;
  score: Score;
  timeDelta: number;
  cancelled?: boolean;
};

export type LastChallenge = {
  challenger: Challenger;
  scoreRallyStart: Score;
  teamThatLastScored: TeamReference;
  timestamp: number;
  challengeType: SamsChallengeType;
  timeDelta: number;
  setScoreAfterChallenge?: Score;
  durationMs?: number;
  challengeMoment?: SamsChallengeMoment;
  outcome?: SamsChallengeOutcome;
  declined?: boolean;
  unresolved?: boolean;
  successful?: boolean;
};

export type SamsChallengeType =
  | "BALL_IN_OUT"
  | "BLOCK_TOUCH"
  | "NET_FAULT"
  | "ANTENNA_CONTACT"
  | "FOOT_FAULT_BASE_LINE"
  | "FOOT_FAULT_ATTACK_LINE"
  | "FOOT_FAULT_HALFWAY_LINE"
  | "GROUND_CONTACT"
  | "LAST_TOUCH";

export type SamsChallengeOutcome =
  | "POINT_REVERSED"
  | "DOUBLE_FAULT"
  | "CONFIRMED"
  | "POINT_TEAM1"
  | "POINT_TEAM2";

export type SamsChallengeMoment =
  | "DURING_RALLY"
  | "AFTER_RALLY"
  | "AFTER_CHALLENGE";

export type Sanctions = {
  [key in TeamReference]: Sanction[];
};

export type Substitutions = {
  [key in TeamReference]: Substitution[];
};
